import { useMutation, useQuery, type UseQueryOptions } from '@tanstack/react-query';
import axios from 'axios';
import axiosInstance from './axiosInstance';
import { request } from '@/services/request';
import type {
  WorkOrderCommentCreateRequest,
  WorkOrderCommentGetParams,
  WorkOrderCommentListResponse,
  WorkOrderCommentResponse,
  WorkOrderGetByIdResponse,
} from '@/types/workOrder.types';
import type { GenericErrorResponse, GenericErrorResponseError } from '@/types/api.types';

type WorkOrderByIdQueryOptions = Omit<
  UseQueryOptions<WorkOrderGetByIdResponse, GenericErrorResponseError>,
  'queryKey' | 'queryFn' | 'enabled'
>;

export const useGetWorkOrderById = (workOrderId: string | undefined, options?: WorkOrderByIdQueryOptions) => {
  return useQuery<WorkOrderGetByIdResponse, GenericErrorResponseError>({
    queryKey: ['workOrder', workOrderId],
    queryFn: async () => {
      if (!workOrderId) throw new Error('Work Order ID is required');
      const response = await axios.get<WorkOrderGetByIdResponse>(`/work-orders/${workOrderId}`);
      return response.data;
    },
    enabled: !!workOrderId,
    ...options,
  });
};

export const fetchWorkOrderComments = (workOrderId: string, params?: WorkOrderCommentGetParams) =>
  request(() =>
    axiosInstance.get<WorkOrderCommentListResponse>(`/work-orders/${encodeURIComponent(workOrderId)}/comments`, {
      params,
    })
  );

export const useCreateWorkOrderComment = () =>
  useMutation<WorkOrderCommentResponse, GenericErrorResponse, { id: string; data: WorkOrderCommentCreateRequest }>({
    mutationFn: ({ id, data }) =>
      request(() =>
        axiosInstance.post<WorkOrderCommentResponse>(`/work-orders/${encodeURIComponent(id)}/comments`, data)
      ),
  });
